import React from 'react'
import styled from 'styled-components'
import { SpaceResponsive, Title } from '../../Pages/NewApp/NewAppBannerPage'

// 씬플레이 큐레이션 섹션 미리보기 (collectionName 'sceneplay').
// 카드 한 장 = 캐릭터 한 명. 앱 카드(SPDiscoverNewCategoryFeaturedACollectionViewCell 의 씬플레이 카드)처럼
// 고른 장소 배경(9:16) 위에 고른 표정의 스탠딩을 세우고, 이름 · 카피 · 태그를 그림 안 아래에 얹는다.
//   - 작업 버전 : 어드민 API 응답의 collection (scenePlayBackgroundImg · scenePlaySpriteImg · 카피 · 태그)
//   - 라이브 버전 : Stipop API v2 응답의 collection. 스탠딩을 아직 내려주지 않아 배경(uploadImg)만 보이고,
//     카피 소스 · 태그가 없으면 소개(intro)만 보인다.
const localized = (assistant, lang, key) => {
  if (assistant.assistantInfo && Array.isArray(assistant.assistantInfo)) {
    const info = assistant.assistantInfo.filter(a => a.lang === lang)[0]
    if (info && info[key]) return info[key]
  }
  return assistant[key] || ''
}

// 서사의 {{user}} 자리. 앱은 이용자 닉네임을 넣고, 없으면 캐릭터 언어의 이 낱말을 쓴다.
const NARRATIVE_USER_WORD = { ko: '당신', ja: 'あなた', vi: 'bạn', es: 'tú' }

// 첫 문장: 개행이 있으면 그 앞까지, 없으면 첫 종결 부호까지(부호 포함), 둘 다 없으면 전문.
const firstSentence = text => {
  const newline = text.search(/[\r\n]/)
  if (newline >= 0) return text.slice(0, newline)
  const terminator = text.search(/[。.？！?!]/)
  return terminator >= 0 ? text.slice(0, terminator + 1) : text
}

// 앱 카피 순서 : 서사 첫 문장 → 첫 상황 → 첫 메시지 → 소개.
const copyOf = (assistant, lang) => {
  const narrative = String(assistant.narrativeRelation || '').trim()
  const narrativeLine = narrative
    ? firstSentence(narrative)
        .split('{{user}}')
        .join(
          NARRATIVE_USER_WORD[String(assistant.mainLang || '').toLowerCase()] ||
            'you'
        )
        .trim()
    : ''
  const candidates = [
    narrativeLine,
    assistant.firstSituation,
    assistant.firstMessage,
    localized(assistant, lang, 'intro'),
  ]
  for (const candidate of candidates) {
    const text = String(candidate || '').trim()
    if (text) return text
  }
  return ''
}

// 태그는 쉼표로 구분한 한 줄. 앱처럼 앞의 2개만 쓴다.
const TAG_DISPLAY_LIMIT = 2
const tagsOf = assistant =>
  String(assistant.tags || '')
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean)
    .slice(0, TAG_DISPLAY_LIMIT)
    .map(tag => `#${tag}`)

// 앱 카드 실제 크기(pt). 그 크기로 그린 뒤 `width` 에 맞춰 비율대로만 줄인다.
const APP_CARD_WIDTH = 149
const APP_CARD_HEIGHT = 265

// 카드 한 장. 편집 화면의 무대 미리보기도 이것을 쓴다.
//   assistant : 카드 데이터. spriteImg · backgroundImg 를 주면 그것을 먼저 쓴다(편집 중 고른 것)
export const ScenePlayCardItem = props => {
  const { assistant, lang, width = 135, spriteImg, backgroundImg } = props
  const background =
    backgroundImg ||
    assistant.scenePlayBackgroundImg ||
    assistant.uploadImg ||
    assistant.profileImg
  const sprite = spriteImg || assistant.scenePlaySpriteImg
  const tags = tagsOf(assistant)
  const scale = width / APP_CARD_WIDTH

  return (
    <CardBox
      style={{ width, height: Math.round(APP_CARD_HEIGHT * scale) }}
    >
      <Card style={{ transform: `scale(${scale})` }}>
        {background && <img className="background" src={background} alt="" />}
        {sprite && <img className="sprite" src={sprite} alt="" />}
        <Scrim />
        <Caption>
          <CardName>{localized(assistant, lang, 'assistantName') || '-'}</CardName>
          <CardCopy>{copyOf(assistant, lang)}</CardCopy>
          {tags.length > 0 && (
            <TagRow>
              {tags.map(tag => (
                <TagChip key={tag}>{tag}</TagChip>
              ))}
            </TagRow>
          )}
        </Caption>
      </Card>
    </CardBox>
  )
}

const ScenePlayCard = props => {
  const { title, assistantList, lang } = props
  const list = Array.isArray(assistantList) ? assistantList : []

  return (
    <>
      <Title>{title}</Title>
      <SpaceResponsive height={16} />
      <Wrapper>
        <CardRow>
          {list.map((assistant, index) => (
            <ScenePlayCardItem
              key={assistant.seq || index}
              assistant={assistant}
              lang={lang}
            />
          ))}
        </CardRow>
      </Wrapper>
      <SpaceResponsive height={34} />
    </>
  )
}

export default ScenePlayCard

const Wrapper = styled.div`
  width: 100%;
  overflow-x: scroll;
  position: relative;
`

const CardRow = styled.div`
  display: flex;
  column-gap: 8px;
`

const CardBox = styled.div`
  flex: none;
  position: relative;
  overflow: hidden;
`

const Card = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${APP_CARD_WIDTH}px;
  height: ${APP_CARD_HEIGHT}px;
  transform-origin: top left;
  border-radius: 12px;
  overflow: hidden;
  background-color: #ddd;

  .background {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .sprite {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    aspect-ratio: 3 / 4;
    object-fit: contain;
  }
`

const Scrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 50%;
  background: linear-gradient(
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0.45),
    rgba(0, 0, 0, 0.78)
  );
`

const Caption = styled.div`
  position: absolute;
  left: 9px;
  right: 9px;
  bottom: 10px;
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  color: #fff;
  font-family: Poppins;
`

const CardName = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CardCopy = styled.div`
  font-size: 10px;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.85);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
`

const TagRow = styled.div`
  display: flex;
  column-gap: 4px;
  min-width: 0;
`

const TagChip = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  height: 14px;
  padding: 0 5px;
  border-radius: 7px;
  background-color: rgba(255, 255, 255, 0.2);
  font-size: 9px;
  line-height: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:first-child {
    flex-shrink: 0;
    max-width: 100%;
  }
`
